/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';

import { AlertDialog } from 'src/components';
import { localize } from 'src/constants';
import useUserInfo from 'src/hooks/user/use-user-info';
import { useLocation } from 'react-router';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';

interface Props {}

const { actions: lastRouteActions } = slices.lastRoute;
const { actions: userActions, selectors: userSelectors } = slices.user;

const getMidnightAfterLogin = (loggedInTime?: number) => {
  if (!loggedInTime) {
    return undefined;
  }

  const loggedInDate = new Date(loggedInTime);
  const midnightAfterLogin = new Date(loggedInDate);
  midnightAfterLogin.setHours(24, 0, 0, 0);

  return midnightAfterLogin.toISOString();
};

export const SessionExpiredDialog = ({ ...props }: Props) => {
  const { checkUserTokenV2 } = useUserInfo();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector(userSelectors.selectUserInfo);

  const [showDialog, setShowDialog] = useState<boolean>(false);

  const validateUser = async () => {
    const isValid = await checkUserTokenV2();
    if (isValid) {
      return;
    } else {
      setShowDialog(true);
    }
  };

  const handleRedirect = () => {
    dispatch(userActions.clearSession());
    dispatch(lastRouteActions.clearLastRoute());
    localStorage.removeItem('persist:last-route');
    setShowDialog(false);
    window.location.href = '/login';
  };

  useEffect(() => {
    //dont run this on login view XD
    if (pathname !== '/login') {
      validateUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (pathname === '/login' || !userDetails?.id) {
      setShowDialog(false);
      return;
    }

    //get expired at data
    const expiredAt =
      userDetails?.expired_at ||
      userDetails?.expires_at ||
      getMidnightAfterLogin(userDetails?.loggedInTime);

    if (!expiredAt) {
      setShowDialog(true);
      return;
    }

    const expiredAtTime = new Date(expiredAt).getTime();

    if (Number.isNaN(expiredAtTime)) {
      setShowDialog(true);
      return;
    }

    const remainingTime = expiredAtTime - Date.now();

    if (remainingTime <= 0) {
      setShowDialog(true);
      return;
    }

    //logout user if token is expired based on set timeout
    const timeout = window.setTimeout(() => {
      setShowDialog(true);
    }, remainingTime);

    return () => window.clearTimeout(timeout);
  }, [
    pathname,
    userDetails?.expired_at,
    userDetails?.expires_at,
    userDetails?.id
  ]);

  const RenderButtons = () => (
    <>
      <Button onClick={() => handleRedirect()} color="secondary" autoFocus>
        Close
      </Button>
    </>
  );

  return (
    <AlertDialog
      title="Session Expired"
      customButtons={<RenderButtons />}
      subTitle={localize.WARN_SESSION_EXPIRED}
      isVisible={showDialog}
      {...props}
    />
  );
};
